// DRUM international delivery: for visitors outside South Africa, shows the delivery price and cart total
// in their own currency. Checkout still charges rand (Shopify only converts with Shopify Payments).
// Delivery prices come from drum-intl-rates.json (built by analysis/intl_delivery_rates.py),
// exchange rates from a free daily feed. Text is edited in Theme settings → International delivery.
(() => {
  const COUNTRY_KEY = 'drum-country';
  const FX_KEY = 'drum-fx';
  const DAY = 864e5;
  const FX_URLS = [
    'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/zar.min.json',
    'https://latest.currency-api.pages.dev/v1/currencies/zar.min.json',
  ];
  const CURRENCIES = {
    AD: 'EUR', AE: 'AED', AT: 'EUR', AU: 'AUD', BE: 'EUR', BW: 'BWP', CA: 'CAD', CH: 'CHF', CN: 'CNY',
    CZ: 'CZK', DE: 'EUR', DK: 'DKK', ES: 'EUR', FI: 'EUR', FR: 'EUR', GB: 'GBP', GH: 'GHS', GR: 'EUR',
    HK: 'HKD', IE: 'EUR', IN: 'INR', IT: 'EUR', JP: 'JPY', KE: 'KES', KR: 'KRW', LS: 'LSL', MW: 'MWK',
    MY: 'MYR', MZ: 'MZN', NA: 'NAD', NG: 'NGN', NL: 'EUR', NO: 'NOK', NZ: 'NZD', PL: 'PLN', PT: 'EUR',
    RW: 'RWF', SE: 'SEK', SG: 'SGD', SN: 'XOF', SZ: 'SZL', TZ: 'TZS', UG: 'UGX', US: 'USD', ZM: 'ZMW',
  };

  // localStorage can be blocked (private windows), so every read and write is guarded.
  const store = {
    get(key) {
      try {
        return JSON.parse(localStorage.getItem(key));
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (e) {}
    },
  };

  const markOverseas = (code) => document.documentElement.classList.toggle('drum-overseas', code !== 'ZA');

  // The visitor's country: their own choice if they made one, otherwise Shopify's location guess (kept a day).
  let countryPromise;
  const country = () =>
    (countryPromise ??= (async () => {
      // For testing: ?drum_country=GB on any page acts as a visitor from that country (?drum_country=ZA to undo).
      const forced = new URLSearchParams(location.search).get('drum_country');
      if (/^[A-Za-z]{2}$/.test(forced || '')) {
        store.set(COUNTRY_KEY, { code: forced.toUpperCase(), at: Date.now(), chosen: true });
        return forced.toUpperCase();
      }
      const saved = store.get(COUNTRY_KEY);
      if (saved?.code && (saved.chosen || Date.now() - saved.at < DAY)) return saved.code;
      try {
        const response = await fetch('/browsing_context_suggestions.json?country[enabled]=true');
        const code = (await response.json()).detected_values?.country?.handle || 'ZA';
        store.set(COUNTRY_KEY, { code, at: Date.now() });
        return code;
      } catch (e) {
        return 'ZA';
      }
    })());

  const chooseCountry = (code) => {
    store.set(COUNTRY_KEY, { code, at: Date.now(), chosen: true });
    countryPromise = Promise.resolve(code);
    markOverseas(code);
    document.querySelectorAll('drum-intl-estimate').forEach((el) => el.render());
  };

  let fxPromise;
  const exchangeRates = () =>
    (fxPromise ??= (async () => {
      const saved = store.get(FX_KEY);
      if (saved && Date.now() - saved.at < DAY / 2) return saved.rates;
      for (const url of FX_URLS) {
        try {
          const rates = (await (await fetch(url)).json()).zar;
          store.set(FX_KEY, { rates, at: Date.now() });
          return rates;
        } catch (e) {}
      }
      return saved?.rates || null;
    })());

  const tables = {};
  const deliveryTable = (url) => (tables[url] ??= fetch(url).then((r) => r.json()).catch(() => null));

  const rand = (cents) => `R ${Math.round(cents / 100).toLocaleString('en-US')}`;
  const regionName = (code) => {
    try {
      return new Intl.DisplayNames(['en'], { type: 'region' }).of(code);
    } catch (e) {
      return code;
    }
  };
  const local = (cents, currency, fx) => {
    const rate = fx?.[currency.toLowerCase()];
    if (!rate) return null;
    // "A$" and "CA$" rather than a bare "$"; the local sign (₦, KSh) where English only has the code.
    const format = (currencyDisplay) =>
      new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay, maximumFractionDigits: 0 }).format(
        (cents / 100) * rate
      );
    const symbol = format('symbol');
    return symbol.startsWith(currency) ? format('narrowSymbol') : symbol;
  };
  const fill = (template, values) => template.replace(/\[(\w+)\]/g, (match, key) => values[key] ?? match);

  class DrumIntlEstimate extends HTMLElement {
    connectedCallback() {
      this.render();
    }

    async render() {
      const code = await country();
      markOverseas(code);
      if (code === 'ZA') {
        this.hidden = this.dataset.context === 'cart';
        if (this.dataset.context === 'product') this.textContent = this.dataset.defaultText;
        return;
      }

      const [table, fx] = await Promise.all([deliveryTable(this.dataset.ratesUrl), exchangeRates()]);
      const entry = table?.countries?.[code];
      const currency = CURRENCIES[code] || 'USD';
      const grams = Math.max(parseInt(this.dataset.weight || '0', 10), 1);
      const band = entry?.rates.find(([max]) => grams <= max);
      const shipping = band ? band[1] * 100 : null;
      const values = { country: regionName(code), service: entry?.service || '' };

      if (this.dataset.context === 'product') {
        if (!entry) {
          this.textContent = fill(this.dataset.noDeliveryText, values);
        } else {
          values.rand = rand(shipping);
          values.price = local(shipping, currency, fx) || values.rand;
          this.textContent = fill(this.dataset.productText, values);
        }
        return;
      }

      this.renderCountrySelect(code, table);
      const total = parseInt(this.dataset.total, 10) + (shipping || 0);
      const about = (cents) => {
        const price = shipping && local(cents, currency, fx);
        return price && fill(this.dataset.aboutText, { price });
      };
      const localShipping = about(shipping);
      const localTotal = about(total);
      values.rand = rand(total);

      let note = fill(this.dataset.noDeliveryText, values);
      if (entry) note = shipping ? `${entry.service}. ${this.dataset.dutiesText}` : fill(this.dataset.checkoutText, values);
      this.querySelector('[data-shipping]').textContent = localShipping || (shipping ? rand(shipping) : '');
      this.querySelector('[data-shipping-rand]').textContent = localShipping ? `(${rand(shipping)})` : '';
      this.querySelector('[data-service]').textContent = note;
      this.querySelector('[data-total-local]').textContent = localTotal || '';
      this.querySelector('[data-total-row]').hidden = !localTotal;
      this.querySelector('[data-pay]').textContent = shipping ? fill(this.dataset.payText, values) : '';
      this.hidden = false;
    }

    renderCountrySelect(code, table) {
      const select = this.querySelector('select');
      if (select.options.length === 0) {
        const codes = Object.keys(table?.countries || {}).concat('ZA');
        if (!codes.includes(code)) codes.push(code);
        codes
          .map((c) => [c, regionName(c)])
          .sort((a, b) => a[1].localeCompare(b[1]))
          .forEach(([c, name]) => select.add(new Option(name, c)));
        select.addEventListener('change', () => chooseCountry(select.value));
      }
      select.value = code;
    }
  }

  if (!customElements.get('drum-intl-estimate')) customElements.define('drum-intl-estimate', DrumIntlEstimate);
  country().then(markOverseas);
})();
